/**
 * Carrom Physics Engine
 *
 * Lightweight 2D physics engine for circle collisions on a flat surface.
 * Handles: position updates, friction, wall bouncing, circle-circle elastic
 * collisions, pocket detection, and stop detection.
 *
 * Runs at 60fps fixed timestep for deterministic results.
 */

import {
  PhysicsBody,
  PhysicsConfig,
  DEFAULT_PHYSICS_CONFIG,
  Vector2D,
} from './types';

export class CarromPhysics {
  private bodies: PhysicsBody[] = [];
  private config: PhysicsConfig;
  private pocketedThisFrame: PhysicsBody[] = [];
  private onPocketed?: (body: PhysicsBody) => void;

  constructor(config: PhysicsConfig = DEFAULT_PHYSICS_CONFIG) {
    this.config = config;
  }

  // =========================================================================
  // Body Management
  // =========================================================================

  addBody(body: PhysicsBody): void {
    this.bodies.push(body);
  }

  removeBody(id: string): void {
    this.bodies = this.bodies.filter((b) => b.id !== id);
  }

  getBody(id: string): PhysicsBody | undefined {
    return this.bodies.find((b) => b.id === id);
  }

  getBodies(): PhysicsBody[] {
    return this.bodies;
  }

  getActiveBodies(): PhysicsBody[] {
    return this.bodies.filter((b) => !b.isStatic && !b.pocketed);
  }

  setOnPocketed(callback: (body: PhysicsBody) => void): void {
    this.onPocketed = callback;
  }

  clearBodies(): void {
    this.bodies = [];
  }

  // =========================================================================
  // Physics Step
  // =========================================================================

  /**
   * Advance physics simulation by dt seconds.
   * Uses fixed sub-stepping for stability with high velocities.
   */
  step(dt: number): PhysicsBody[] {
    this.pocketedThisFrame = [];
    const activeBodies = this.getActiveBodies();

    if (activeBodies.length === 0) return this.pocketedThisFrame;

    // Fixed sub-step at 1/120s for smooth, stable simulation
    const fixedSubStep = 1 / 120;
    let remaining = dt;

    while (remaining > 0) {
      const subDt = Math.min(remaining, fixedSubStep);
      this.substep(subDt, activeBodies);
      remaining -= subDt;
    }

    return this.pocketedThisFrame;
  }

  private substep(dt: number, activeBodies: PhysicsBody[]): void {
    // 1. Apply frame-rate-independent friction (exponential decay)
    for (const body of activeBodies) {
      if (!body.pocketed) {
        this.applyFriction(body, dt);
      }
    }

    // 2. Update positions
    for (const body of activeBodies) {
      if (!body.pocketed) {
        body.x += body.vx * dt;
        body.y += body.vy * dt;
      }
    }

    // 3. Wall collisions
    for (const body of activeBodies) {
      if (!body.pocketed) {
        this.resolveWallCollision(body);
      }
    }

    // 4. Circle-circle collisions (multiple passes for stability)
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < activeBodies.length; i++) {
        for (let j = i + 1; j < activeBodies.length; j++) {
          const a = activeBodies[i];
          const b = activeBodies[j];
          if (!a.pocketed && !b.pocketed) {
            this.resolveCircleCollision(a, b);
          }
        }
      }
    }

    // 5. Pocket detection
    for (const body of activeBodies) {
      if (!body.pocketed) {
        this.checkPockets(body);
      }
    }

    // 6. Stop slow bodies
    for (const body of activeBodies) {
      if (!body.pocketed) {
        const speed = Math.sqrt(body.vx * body.vx + body.vy * body.vy);
        if (speed < this.config.stopThreshold) {
          body.vx = 0;
          body.vy = 0;
        }
      }
    }
  }

  // =========================================================================
  // Friction
  // =========================================================================

  /**
   * Apply frame-rate-independent friction.
   * Uses exponential decay: v *= friction^(dt * 60)
   * This ensures the same deceleration regardless of frame rate.
   */
  applyFriction(body: PhysicsBody, dt: number): void {
    const factor = Math.pow(this.config.friction, dt * 60);
    body.vx *= factor;
    body.vy *= factor;
  }

  // =========================================================================
  // Wall Collision
  // =========================================================================

  resolveWallCollision(body: PhysicsBody): void {
    const minX = this.config.borderWidth + body.radius;
    const maxX = this.config.boardSize - this.config.borderWidth - body.radius;
    const minY = this.config.borderWidth + body.radius;
    const maxY = this.config.boardSize - this.config.borderWidth - body.radius;

    // Check if near a pocket — don't bounce if heading into pocket
    if (this.isNearPocket(body)) {
      return;
    }

    if (body.x < minX) {
      body.x = minX;
      body.vx = Math.abs(body.vx) * this.config.restitution;
    } else if (body.x > maxX) {
      body.x = maxX;
      body.vx = -Math.abs(body.vx) * this.config.restitution;
    }

    if (body.y < minY) {
      body.y = minY;
      body.vy = Math.abs(body.vy) * this.config.restitution;
    } else if (body.y > maxY) {
      body.y = maxY;
      body.vy = -Math.abs(body.vy) * this.config.restitution;
    }
  }

  private isNearPocket(body: PhysicsBody): boolean {
    for (const pocket of this.config.pocketPositions) {
      const dx = body.x - pocket.x;
      const dy = body.y - pocket.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < this.config.pocketRadius + body.radius) {
        return true;
      }
    }
    return false;
  }

  // =========================================================================
  // Circle-Circle Elastic Collision
  // =========================================================================

  resolveCircleCollision(a: PhysicsBody, b: PhysicsBody): void {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const minDist = a.radius + b.radius;

    if (dist >= minDist || dist === 0) return;

    // Normal vector
    const nx = dx / dist;
    const ny = dy / dist;

    // Relative velocity along normal
    const dvn = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;

    // Only resolve if approaching
    if (dvn <= 0) return;

    // Impulse scalar (correct elastic collision with restitution)
    const e = this.config.restitution;
    const invMassSum = 1 / a.mass + 1 / b.mass;
    const j = (1 + e) * dvn / invMassSum;

    // Apply impulse
    a.vx -= (j / a.mass) * nx;
    a.vy -= (j / a.mass) * ny;
    b.vx += (j / b.mass) * nx;
    b.vy += (j / b.mass) * ny;

    // Separate overlapping bodies
    const overlap = minDist - dist;
    const totalMass = a.mass + b.mass;
    const separationFactor = 1.01; // slight extra push to prevent sticking
    a.x -= (overlap * separationFactor * (b.mass / totalMass)) * nx;
    a.y -= (overlap * separationFactor * (b.mass / totalMass)) * ny;
    b.x += (overlap * separationFactor * (a.mass / totalMass)) * nx;
    b.y += (overlap * separationFactor * (a.mass / totalMass)) * ny;
  }

  // =========================================================================
  // Pocket Detection
  // =========================================================================

  checkPockets(body: PhysicsBody): boolean {
    for (const pocket of this.config.pocketPositions) {
      const dx = body.x - pocket.x;
      const dy = body.y - pocket.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.config.pocketRadius) {
        body.pocketed = true;
        body.vx = 0;
        body.vy = 0;
        body.isStatic = true;
        this.pocketedThisFrame.push(body);

        if (this.onPocketed) {
          this.onPocketed(body);
        }
        return true;
      }
    }
    return false;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  areAllStopped(): boolean {
    const activeBodies = this.getActiveBodies();
    for (const body of activeBodies) {
      const speed = Math.sqrt(body.vx * body.vx + body.vy * body.vy);
      if (speed > this.config.stopThreshold) {
        return false;
      }
    }
    return true;
  }

  /**
   * Apply a shot to the striker body.
   * @param strikerId - ID of the striker body
   * @param angle - Direction angle in radians
   * @param power - Power from 0 to 1
   */
  applyShot(strikerId: string, angle: number, power: number): void {
    const striker = this.getBody(strikerId);
    if (!striker) return;

    const clampedPower = Math.max(0, Math.min(1, power));
    const speed = clampedPower * this.config.maxSpeed;

    striker.vx = Math.cos(angle) * speed;
    striker.vy = Math.sin(angle) * speed;
    striker.isStatic = false;
    striker.pocketed = false;
  }

  /**
   * Reset a body's position and velocity.
   */
  resetBody(id: string, x: number, y: number): void {
    const body = this.getBody(id);
    if (!body) return;

    body.x = x;
    body.y = y;
    body.vx = 0;
    body.vy = 0;
    body.isStatic = false;
    body.pocketed = false;
  }

  // =========================================================================
  // Utility
  // =========================================================================

  static distance(a: Vector2D, b: Vector2D): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static normalize(v: Vector2D): Vector2D {
    const len = Math.sqrt(v.x * v.x + v.y * v.y);
    if (len === 0) return { x: 0, y: 0 };
    return { x: v.x / len, y: v.y / len };
  }

  static angleBetween(from: Vector2D, to: Vector2D): number {
    return Math.atan2(to.y - from.y, to.x - from.x);
  }

  /**
   * Update physics config at runtime (e.g. when powder or speed changes).
   */
  updateConfig(partial: Partial<PhysicsConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  getConfig(): PhysicsConfig {
    return { ...this.config };
  }
}
